import { useState, useRef, useEffect, useCallback } from 'react'
import { Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import type { Message } from '@/types/api'

interface GapChatProps {
  /** Singular suggested question — populates the textarea on click. */
  suggestedQuestion?: string | null
  /** Messages already exchanged inside this gap thread. */
  messages: Message[]
  /** True while a send is in flight — disables the composer. */
  sending: boolean
  /** Whether this row resolved — disables the composer with a hint. */
  resolved: boolean
  /** Submit handler. Receives the trimmed text. */
  onSend: (content: string) => void
}

/**
 * Per-gap chat thread + composer. The chat is scoped to a single gap so
 * the page can dispatch messages with `activeGapId` set to this gap. The
 * suggested chip — singular, per FRONTEND-SPEC — populates the textarea
 * instead of auto-sending; the user still has to confirm.
 */
export function GapChat({
  suggestedQuestion,
  messages,
  sending,
  resolved,
  onSend,
}: GapChatProps) {
  const [draft, setDraft] = useState('')
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const canSend = draft.trim().length > 0 && !sending && !resolved

  const handleSend = useCallback(() => {
    if (!canSend) return
    onSend(draft.trim())
    setDraft('')
  }, [canSend, draft, onSend])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend]
  )

  const handleChipClick = useCallback(() => {
    if (!suggestedQuestion) return
    setDraft(suggestedQuestion)
    textareaRef.current?.focus()
  }, [suggestedQuestion])

  // Auto-grow the textarea up to a sensible cap so paragraphs aren't
  // squashed into a single-line box.
  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 240)}px`
  }, [draft])

  return (
    <div className="flex flex-col gap-3">
      {messages.length > 0 && (
        <div
          data-testid="gap-chat-thread"
          className="flex flex-col gap-2"
        >
          {messages.map((m) => (
            <ChatBubble key={m.id} message={m} />
          ))}
        </div>
      )}

      {suggestedQuestion && !resolved && (
        <button
          type="button"
          onClick={handleChipClick}
          data-testid="gap-suggested-chip"
          className={cn(
            'inline-flex items-center gap-2 self-start text-xs text-text-secondary',
            'px-3 py-1.5 rounded-full border border-border-default bg-bg-elevated',
            'hover:border-accent hover:text-text-primary transition-colors max-w-full'
          )}
        >
          <Sparkles size={12} className="text-accent shrink-0" />
          <span className="truncate">{suggestedQuestion}</span>
        </button>
      )}

      <div className="flex items-stretch gap-2">
        <textarea
          ref={textareaRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={resolved}
          placeholder={
            resolved
              ? 'This gap is resolved.'
              : 'Type your answer… (Cmd+Enter to send)'
          }
          rows={2}
          className={cn(
            'flex-1 resize-none rounded-md border border-border-default bg-bg-primary',
            'px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary',
            'focus:outline-none focus:border-accent transition-colors',
            'disabled:opacity-60 disabled:cursor-not-allowed'
          )}
        />
        <Button
          type="button"
          onClick={handleSend}
          disabled={!canSend}
          size="sm"
          className="self-end"
          data-testid="gap-chat-send"
        >
          {sending ? 'Sending…' : 'Send'}
        </Button>
      </div>
    </div>
  )
}

function ChatBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user'
  return (
    <div
      data-testid={`gap-chat-msg-${message.role}`}
      className={cn(
        'max-w-[80%] rounded-lg px-3 py-2 text-sm leading-relaxed',
        isUser
          ? 'self-end bg-accent/10 text-text-primary border border-accent/30'
          : 'self-start bg-bg-elevated text-text-primary border border-border-default'
      )}
    >
      <p className="whitespace-pre-wrap break-words">{message.content}</p>
    </div>
  )
}
